import config from '../../config';
import { allowedFileExtensions } from '@features/appRun/utils/renderResults';
import { IFileInIframe, IFrameRequestType, IFrameParentMessageType } from './iframe.types';

interface IOptions {
    job?: { uuid: string; auth_token: string; app_uri: string; created_at: string };
    user?: { email: string; handle: string };
    iframeId: string;
    file: IFileInIframe | null;
    growHeightToFitContent?: boolean;
    enableDebouncedAutoResize?: boolean;
    centerImage?: boolean;
}

export function getIframeCommunicationScript(options: IOptions): Uint8Array {
    const { job, user, iframeId, file } = options;

    // Note: all custom functions are defined in a closed scope { } and only exposed to rest of iframe HTML through
    // specific assignments on "window"
    return new TextEncoder().encode(`\
<script>
    {
        window._biolib_internal = {
            requestIdToPromise: {},
            file: ${JSON.stringify(file)},
            utilFunctions: {},
            enableDebouncedAutoResize: ${JSON.stringify(options.enableDebouncedAutoResize || false)},
            centerImage: ${JSON.stringify(options.centerImage || false)},
        }

        async function postMessageToParent(type, payload, jobProgressHandler) {
            const requestId = crypto.randomUUID();
            const promise = new Promise((resolve, reject) => {
                window._biolib_internal.requestIdToPromise[requestId] = { resolve, reject, jobProgressHandler };
            });
            window.parent.postMessage({ requestId, type, payload, iframeId: '${iframeId}' }, '${location.origin}');
            return promise;
        }

        function sendResize() {
            const payload = { height: document.body.scrollHeight, width: document.body.scrollWidth };
            postMessageToParent('${IFrameRequestType.resizeIframe}', payload);
        }

        window._biolib_internal.sendResize = sendResize;

        const processMessage = (event) => {
            if (
                event.origin === '${config.baseURL}' &&
                typeof event.data?.requestId === 'string' &&
                (
                    event.data.type === '${IFrameParentMessageType.generic}' ||
                    event.data.type === '${IFrameParentMessageType.jobProgress}'
                )
            ) {
                const promise = window._biolib_internal.requestIdToPromise[event.data.requestId];
                if (promise) {
                    if (event.data.type === '${IFrameParentMessageType.jobProgress}') {
                        if (typeof promise.jobProgressHandler === 'function') {
                            try {
                                promise.jobProgressHandler(event.data.payload);
                            } catch (error) {
                                console.error('Calling jobProgress failed with error: ', error);
                            }
                        }
                    } else {
                        if ('error' in event.data.payload) {
                            promise.reject(event.data.payload.error);
                        } else {
                            promise.resolve(event.data.payload.result);
                        }
                        delete window._biolib_internal.requestIdToPromise[event.data.requestId];
                    }
                } else {
                    console.warn('Iframe received an event with no promise to resolve: ', event);
                }
            } else if (event.origin === '${config.baseURL}' && event.data?.type === '${IFrameParentMessageType.triggerResize}') {
                sendResize();
            } else if (event.origin === '${config.baseURL}' && event.data?.type === '${IFrameParentMessageType.utilFunctionInvocation}') {
                try {
                    const { utilFunctionName, args, requestId } = event.data;
                    const utilFunction = window._biolib_internal.utilFunctions?.[utilFunctionName];
                    if (!utilFunction) {
                        throw new Error(\`Utility function '\${utilFunctionName}' not found\`);
                    }
                    const result = utilFunction(...args);
                    window.parent.postMessage({
                        type: '${IFrameParentMessageType.utilFunctionResponse}',
                        requestId: requestId,
                        result: result,
                    }, '${location.origin}');
                } catch (error) {
                    window.parent.postMessage({
                        type: '${IFrameParentMessageType.utilFunctionResponse}',
                        requestId: event.data.requestId,
                        error: String(error),
                    }, '${location.origin}');
                }
            } else if (event.origin === '${config.baseURL}') {
                console.warn('Iframe received an unrecognized event: ', event);
            }
        };
        window.addEventListener('message', processMessage, false);

        const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        async function getOutputFileData(path) {
            return postMessageToParent('${IFrameRequestType.getOutputFileData}', { path });
        }

        async function getInputFileData(path) {
            return postMessageToParent('${IFrameRequestType.getInputFileData}', { path });
        }

        async function getFileDataFromDataRecord(options) {
            return postMessageToParent('${IFrameRequestType.getFileDataFromDataRecord}', options);
        }

        async function runJob(payload, jobProgressHandler) {
            if (typeof payload !== 'object') {
                throw new Error('First argument to runJob must be an object.');
            }
            if (jobProgressHandler && typeof jobProgressHandler !== 'function') {
                throw new Error('Second argument to runJob is optional but must be an function if given.');
            }
            const resolvedPayload = { ...payload };
            if (resolvedPayload.experiment) {
                if (resolvedPayload.experimentUuid) {
                    throw new Error('Both experiment and experimentUuid were provided to runJob. Please use only one.');
                }
                if (resolvedPayload.experiment instanceof Experiment) {
                    resolvedPayload.experimentUuid = resolvedPayload.experiment.id;
                } else if (typeof resolvedPayload.experiment === 'string') {
                    if (UUID_REGEX.test(resolvedPayload.experiment)) {
                        resolvedPayload.experimentUuid = resolvedPayload.experiment;
                    } else {
                        const experimentObj = await getExperiment({ uri: resolvedPayload.experiment });
                        resolvedPayload.experimentUuid = experimentObj.id;
                    }
                } else {
                    throw new Error('experiment must be a UUID string, a URI string, or an Experiment instance.');
                }
                delete resolvedPayload.experiment;
            }
            return postMessageToParent('${IFrameRequestType.runJob}', resolvedPayload, jobProgressHandler);
        }

        async function listOutputFilePaths() {
            return postMessageToParent('${IFrameRequestType.listOutputFilePaths}', {});
        }

        class Experiment {
            // Only expose properties that are immutable for the lifetime of the object
            constructor(data) {
                this.id = data.uuid;
                this.name = data.name;
                this.uri = data.uri;
                this.createdAt = data.createdAt;
            }

            async getResults(options) {
                if (options !== undefined && typeof options !== 'object') {
                    throw new Error('First argument to getResults must be an object.');
                }
                if (options) {
                    const allowedKeys = ['status'];
                    const unexpectedKeys = Object.keys(options).filter((key) => !allowedKeys.includes(key));
                    if (unexpectedKeys.length > 0) {
                        throw new Error('Unexpected keys: ' + unexpectedKeys.join(', '));
                    }
                }
                const pageSize = 1000;
                const payload = { id: this.id, page_size: pageSize };
                if (options?.status) {
                    payload.state = options.status;
                }
                const firstPage = await postMessageToParent('${IFrameRequestType.listExperimentResults}', payload);
                const allResults = [...firstPage.results.map((data) => new Result(data))];
                for (let page = 2; page <= firstPage.page_count; page++) {
                    const nextPage = await postMessageToParent('${IFrameRequestType.listExperimentResults}', { ...payload, page });
                    allResults.push(...nextPage.results.map((data) => new Result(data)));
                }
                return allResults;
            }

            toJSON() {
                return {
                    id: this.id,
                    name: this.name,
                    uri: this.uri,
                    createdAt: this.createdAt,
                };
            }
        }

        async function getExperiment(payload) {
            if (typeof payload === 'string') {
                payload = UUID_REGEX.test(payload) ? { id: payload } : { uri: payload };
            }
            if (typeof payload !== 'object') {
                throw new Error('First argument to getExperiment must be a string or an object.');
            }
            const data = await postMessageToParent('${IFrameRequestType.getExperiment}', payload);
            return new Experiment(data);
        }

        async function createExperiment(payload) {
            if (typeof payload !== 'object') {
                throw new Error('First argument to createExperiment must be an object.');
            }
            const data = await postMessageToParent('${IFrameRequestType.createExperiment}', payload);
            return new Experiment(data);
        }

        async function listExperiments(payload) {
            if (payload && typeof payload !== 'object') {
                throw new Error('First argument to listExperiments must be an object.');
            }
            const response = await postMessageToParent('${IFrameRequestType.listExperiments}', payload || {});
            return {
                ...response,
                results: response.results.map((data) => new Experiment(data)),
            };
        }

        async function listExperimentResults(payload) {
            if (typeof payload !== 'object') {
                throw new Error('First argument to listResults must be an object.');
            }
            const response = await postMessageToParent('${IFrameRequestType.listExperimentResults}', payload);
            return {
                ...response,
                results: response.results.map((data) => new Result(data)),
            };
        }

        class LazyLoadedFile {
            // Only expose properties that are immutable for the lifetime of the object
            constructor(data) {
                this.path = data.path;
                this.sizeInBytes = data.sizeInBytes;
                this._resultId = data.resultId;
                this._resultAuthToken = data.resultAuthToken;
            }

            async getData() {
                return postMessageToParent(
                    '${IFrameRequestType.getResultOutputFileData}',
                    { resultId: this._resultId, authToken: this._resultAuthToken, path: this.path },
                );
            }

            toJSON() {
                return {
                    path: this.path,
                    sizeInBytes: this.sizeInBytes,
                };
            }
        }

        class ResultNote {
            // Only expose properties that are immutable for the lifetime of the object
            constructor(data) {
                this.uuid = data.uuid;
                this.note = data.note;
                this.createdAt = data.created_at;
                this.type = data.type;
            }

            toJSON() {
                return {
                    uuid: this.uuid,
                    note: this.note,
                    createdAt: this.createdAt,
                    type: this.type,
                };
            }
        }

        class Result {
            // Only expose properties that are immutable for the lifetime of the object
            constructor(data) {
                this.id = data.id;
                this.authToken = data.authToken;
                this.createdAt = data.createdAt;
                this.appUri = data.appUri;
                this._finishedAt = data.finishedAt || null;
            }

            async _refetch() {
                const data = await postMessageToParent('${IFrameRequestType.getResult}', { resultId: this.id, authToken: this.authToken });
                if (data.authToken) {
                    this.authToken = data.authToken;
                }
                this._finishedAt = data.finishedAt;
                return data;
            }

            async getStatus() {
                const data = await this._refetch();
                return data.state;
            }

            async getExitCode() {
                const data = await this._refetch();
                if (data.finishedAt === null) {
                    throw new Error('Cannot get exit code: result is not finished. Call getStatus() to check the result status.');
                }
                return data.exitCode;
            }

            async getFinishedAt() {
                const data = await this._refetch();
                return data.finishedAt;
            }

            async isFinished() {
                const data = await this._refetch();
                return data.finishedAt !== null;
            }

            async getName() {
                const data = await this._refetch();
                return data.name;
            }

            async* streamLogs() {
                if (!this.authToken) {
                    const data = await this._refetch();
                    if (!data.authToken) {
                        throw new Error('Cannot stream logs: no auth token available.');
                    }
                }

                let full = true;
                while (true) {
                    const response = await postMessageToParent(
                        '${IFrameRequestType.streamResultLogs}',
                        { resultId: this.id, authToken: this.authToken, full },
                    );
                    if (response.logs) {
                        yield response.logs;
                    }
                    if (response.isCompleted) {
                        return;
                    }
                    if (response.isStarted) {
                        full = false;
                    }
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                }
            }

            async getOutputFile(path) {
                if (!path || typeof path !== 'string') {
                    throw new Error('First argument to getOutputFile must be a file path string.');
                }
                if (!this.authToken || !this._finishedAt) {
                    const data = await this._refetch();
                    if (data.finishedAt === null) {
                        throw new Error('Cannot get output file: result is not finished. Call getStatus() to check the result status.');
                    }
                }
                const fileInfo = await postMessageToParent(
                    '${IFrameRequestType.getResultOutputFileInfo}',
                    { resultId: this.id, authToken: this.authToken, path },
                );
                return new LazyLoadedFile(fileInfo);
            }

            async createNote(payload) {
                const data = await postMessageToParent(
                    '${IFrameRequestType.createResultNote}',
                    { ...payload, resultId: this.id, authToken: this.authToken },
                );
                return new ResultNote(data);
            }

            async getNotes(payload) {
                const { types } = payload || {};
                const basePayload = { resultId: this.id, authToken: this.authToken, page_size: 1_000, types };
                const firstPage = await postMessageToParent('${IFrameRequestType.listResultNotes}', { ...basePayload, page: 1 });
                const allNotes = [...firstPage.results.map((data) => new ResultNote(data))];
                for (let page = 2; page <= firstPage.page_count; page++) {
                    const nextPage = await postMessageToParent('${IFrameRequestType.listResultNotes}', { ...basePayload, page });
                    allNotes.push(...nextPage.results.map((data) => new ResultNote(data)));
                }
                return allNotes;
            }

            toJSON() {
                return {
                    id: this.id,
                    createdAt: this.createdAt,
                    appUri: this.appUri,
                };
            }
        }

        async function getResult(resultId, authToken) {
            if (!resultId || typeof resultId !== 'string') {
                throw new Error('First argument to getResult must be a result ID string.');
            }
            const resultData = await postMessageToParent('${IFrameRequestType.getResult}', { resultId, authToken });
            return new Result(resultData);
        }

        class Index {
            // Only expose properties that are immutable for the lifetime of the object
            constructor(data) {
                this.uri = data.uri;
                this.id = data.id;
            }

            toJSON() {
                return {
                    uri: this.uri,
                    id: this.id,
                };
            }
        }

        async function getIndex(uri) {
            if (!uri || typeof uri !== 'string') {
                throw new Error('First argument to getIndex must be a URI string.');
            }
            const indexData = await postMessageToParent('${IFrameRequestType.getIndex}', { uri });
            return new Index(indexData);
        }

        async function queryIndex(payload) {
            return postMessageToParent('${IFrameRequestType.queryIndex}', payload);
        }

        async function getOAuthAccessToken(scope) {
            if (typeof scope !== 'string') {
                throw new Error('scope must be a string');
            }
            return postMessageToParent('${IFrameRequestType.getOAuthAccessToken}', { scope });
        }

        function openUrl(url) {
            postMessageToParent('${IFrameRequestType.openUrl}', { url });
        }

        window.open = (url, name, features) => {
            openUrl(url);
        }

        window.fetch = async function fetch(resource, options) {
            const payload = {
                url: resource,
                method: options?.method ?? 'GET',
                body: options?.body,
                headers: options?.headers,
            };
            const result = await postMessageToParent('${IFrameRequestType.fetch}', payload);
            return new Response(result.body, { status: result.status });
        }

        document.addEventListener('click', (event) => {
            const target = event.target || event.srcElement;
            if (target.tagName === 'A') {
                const url = target.getAttribute('href');
                if (!url) {
                    event.preventDefault();
                    return;
                }
                const downloadAsFilename = target.getAttribute('download');
                if (!downloadAsFilename || url.startsWith('file:///')) {
                    openUrl(url);
                    event.preventDefault();
                } else {
                    const allowedExtensions = ${JSON.stringify(allowedFileExtensions)};
                    const actualFileExtension = downloadAsFilename.toLowerCase().split('.').pop();
                    if (!allowedExtensions.some((allowedExtension) => actualFileExtension === allowedExtension)) {
                        target.setAttribute('download', downloadAsFilename.concat('.data'));
                    }
                }
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.repeat) {
                return;
            }
            if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
                event.preventDefault();
                postMessageToParent('${IFrameRequestType.keyboardShortcut}', { shortcut: 'openSearch' });
            }
        });

        if (${JSON.stringify(options.growHeightToFitContent || false)}) {
            window.addEventListener('load', (event) => {
                const resizeObserver = new ResizeObserver((entries) => {
                    sendResize();
                });
                resizeObserver.observe(document.body);
            })
        }

        if (window._biolib_internal.enableDebouncedAutoResize) {
            window.addEventListener('load', (event) => {
                setTimeout(sendResize, 200);
                setTimeout(sendResize, 3000);
            });
        }

        window.addEventListener('load', (event) => {
            postMessageToParent('${IFrameRequestType.iframeReady}', {});
        });

        window.biolib = {
            experiment: {
                create: createExperiment,
                list: listExperiments,
                listResults: listExperimentResults,
            },
            getExperiment,
            getInputFileData,
            getIndex,
            getResult,
            getOAuthAccessToken,
            getOutputFileData,
            getFileDataFromDataRecord,
            listOutputFilePaths,
            queryIndex,
            runJob,
            filePath: '${file?.path || ''}',
            job: ${JSON.stringify(!job ? null : { id: job.uuid, authToken: job.auth_token, appUri: job.app_uri })},
            result: ${!job ? 'null' : `new Result(${JSON.stringify({ id: job.uuid, authToken: job.auth_token, appUri: job.app_uri, createdAt: job.created_at })})`},
            user: ${JSON.stringify(user || null)},
            // @deprecated optState is deprecated and will be removed in a future release
            optState: {},
        }
    }
</script>
`);
}
